"use client";

import { useEffect, useRef, useState } from "react";

import { useRouter } from "next/navigation";

import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ShoppingBag } from "lucide-react";

import { OptimizedProductImage } from "@/components/product/OptimizedProductImage";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useCart } from "@/contexts/CartContext";
import { formatPrice } from "@/lib/product-utils";
import { CART_PATH } from "@/lib/route-paths";

interface CartDropdownProps {
  isOpen: boolean;
  onViewCart: () => void;
  anchorEl?: HTMLElement | null;
  onClose?: () => void;
}

export function CartDropdown({ isOpen, anchorEl = null, onClose }: CartDropdownProps) {
  const { items, getItemsByRetailer, getTotalPrice, getRetailerSubtotal } = useCart();
  const router = useRouter();

  const itemsByRetailer = getItemsByRetailer();
  const retailers = Object.keys(itemsByRetailer);

  const containerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{
    top: number;
    right: number;
  } | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const updatePosition = () => {
      if (!anchorEl) return;
      const rect = anchorEl.getBoundingClientRect();
      // Use viewport-relative coords for fixed positioning
      const top = rect.bottom + 8;
      const right = Math.max(window.innerWidth - rect.right, 8); // align right edges of button and dropdown
      setPosition({ top, right });
    };
    updatePosition();
    window.addEventListener("scroll", updatePosition, { passive: true });
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, anchorEl]);

  // Close on click outside (bubble phase)
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node | null;
      const container = containerRef.current;
      const anchor = anchorEl;
      const clickedInsideDropdown = !!(container && target && container.contains(target));
      const clickedAnchor = !!(anchor && target && anchor.contains(target as Node));
      if (!clickedInsideDropdown && !clickedAnchor) {
        onClose?.();
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isOpen, anchorEl, onClose]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        ref={containerRef}
        onMouseDown={(e) => e.stopPropagation()}
        className="z-[1000] w-80 rounded-lg border border-gray-200 bg-white shadow-lg"
        style={
          position ? { position: "fixed", top: position.top, right: position.right } : undefined
        }
      >
        {items.length === 0 ? (
          <div className="p-6 text-center">
            <ShoppingBag className="mx-auto mb-3 h-8 w-8 text-gray-300" />
            <p className="text-gray-500">Your bag is empty</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="border-b border-gray-100 px-4 py-3">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-900">Shopping Bag</span>
                <span className="text-sm text-gray-500">{items.length} items</span>
              </div>
            </div>

            {/* Items grouped by retailer */}
            <div className="max-h-80 overflow-y-auto">
              {retailers.map((retailer, retailerIndex) => (
                <div key={retailer} className="px-4 py-3">
                  {retailerIndex > 0 && <Separator className="mb-3" />}

                  {/* Retailer header - reduced visual weight */}
                  <div className="mb-3 flex items-center justify-between">
                    <h4 className="text-xs font-normal uppercase tracking-wider text-gray-400">
                      {retailer}
                    </h4>
                    <span className="text-xs text-gray-400">
                      {formatPrice(getRetailerSubtotal(retailer))}
                    </span>
                  </div>

                  {/* Items for this retailer */}
                  <div className="space-y-3">
                    {itemsByRetailer[retailer].map((item) => (
                      <div key={item.id} className="flex gap-3">
                        <div
                          className={`h-16 w-12 flex-shrink-0 overflow-hidden rounded bg-gray-50${item.isActive === false ? " opacity-50" : ""}`}
                        >
                          <OptimizedProductImage
                            src={item.image}
                            alt={item.name}
                            width={48}
                            height={64}
                            className="h-full w-full object-cover"
                            unoptimized
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          {/* Emphasized brand name */}
                          <p className="mb-0.5 text-xs font-medium uppercase tracking-wider text-gray-700">
                            {item.brand}
                          </p>
                          {/* Emphasized product name */}
                          <h5 className="mb-1 truncate text-sm font-medium text-gray-900">
                            {item.name}
                          </h5>
                          {item.isActive === false && (
                            <p className="mb-1 text-xs text-amber-600">May be unavailable</p>
                          )}
                          <div className="flex items-center justify-start">
                            {/* Minimal price */}
                            <span className="text-sm text-gray-600">{formatPrice(item.price)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Footer with total and view cart button */}
            <div className="border-t border-gray-100 px-4 py-3">
              <div className="mb-3 flex items-center justify-between">
                <span className="font-medium text-gray-900">Total</span>
                <span className="font-medium text-gray-900">{formatPrice(getTotalPrice())}</span>
              </div>
              <Button
                className="w-full bg-gray-900 py-2 text-white hover:bg-gray-800"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  router.push(CART_PATH);
                }}
              >
                View Bag
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </>
        )}
      </motion.div>
    </AnimatePresence>
  );
}

export default CartDropdown;
